import { Controller,useForm } from 'react-hook-form';
import { HiDownload } from 'react-icons/hi';

import { useGetBoardMeetingTypes } from '@/api/use-board-meeting-types';
import { useGetStates } from '@/api/use-states';
import { RhfFilterForm } from '@/components/filters';
import { InMemoryMultiSelectList } from '@/components/form-controls';
import { MultiSelectOption } from '@/components/form-controls/multi-select-list';
import { StateFilter } from '@/components/state-filter';
import { Button } from '@/components-new/button';

import { generateDefaultCalendarFilters } from '../api';
import { useCalendarExport } from '../hooks/use-calendar-export';

export type CalendarFilterData = {
  states: MultiSelectOption<string>[];
  meetingTypes: MultiSelectOption[];
};

type CalendarFiltersProps = {
  defaultValues: CalendarFilterData;
  onSubmit: (filterData: CalendarFilterData) => void;
  // Additional filter context from parent component
  month?: string;
  day?: string;
  tracked?: boolean;
  priority?: boolean;
};

const CalendarFilter = ({ defaultValues, onSubmit, month, day, tracked, priority }: CalendarFiltersProps) => {
  const { control, handleSubmit, reset, watch } = useForm<CalendarFilterData>({ defaultValues });
  const { data: states = [] } = useGetStates();
  const { data: meetingTypes = [] } = useGetBoardMeetingTypes();
  
  // Get current filter values for export
  const currentValues = watch();
  
  // Calendar export functionality
  const { exportCalendar, isExporting } = useCalendarExport({
    filters: {
      month: month || new Date().toISOString().slice(0, 7), // Use provided month or current month
      day: day,
      tracked: tracked,
      priority: priority,
      states: currentValues.states.map(state => state.id),
      meetingTypes: currentValues.meetingTypes.map(type => type.id),
    }
  });
  
  const handleExportClick = async () => {
    await exportCalendar();
  };
  
  return (
    <RhfFilterForm
      watch={watch}
      onReset={() => reset(generateDefaultCalendarFilters())}
      onSubmit={handleSubmit(onSubmit)}
      actions={
        <Button color="secondary" onClick={handleExportClick} disabled={isExporting}>
          <HiDownload/>
          {isExporting ? 'Exporting...' : 'Export'}
        </Button>
      }
    >
      <Controller
        control={control}
        name="states"
        render={({ field: { value, onChange } }) => (
          <StateFilter
            states={states}
            selected={value}
            onSelectionChange={onChange}
          />
        )}
      />
      <Controller
        control={control}
        name="meetingTypes"
        render={({ field: { value, onChange } }) => (
          <InMemoryMultiSelectList
            placeholder="Search by Meeting Type"
            options={meetingTypes}
            value={value}
            onChange={onChange}
          />
        )}
      />
    </RhfFilterForm>
  );
};

export default CalendarFilter;
